/**
 * Sync — the Mercury sync's state, and the exceptions it could not decide.
 *
 * Reached from the profile menu (the operator's decision, 2026-09-13): the
 * banner above every screen already says when something is wrong.
 *
 * THE EXCEPTIONS (2026-09-14)
 *
 * This screen showed only a count, while the list and the resolve route both
 * existed -- a problem the operator could see and not act on. Each exception is
 * now listed with the entry it concerns, what it was and what Mercury now says.
 *
 * MARK HANDLED CHANGES NO BOOKS. §9.1: a divergence is resolved by correcting
 * the entry, and marking the exception handled only takes it off this list. The
 * confirmation says that before the click, or the button reads like an undo.
 */

import { useCan } from '../auth/AuthProvider.js';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api, type ManualSyncResult, type SyncException, type SyncStatus } from '../api.js';
import { formatTimestamp } from '../lib/timezone.js';
import { announceSyncChanged } from '../lib/sync-signal.js';

/** What each kind of exception means, in words. Unknown kinds show their field name. */
const EXCEPTION_LABEL: Record<string, string> = {
  mercury_account_id: 'Mercury Account Not Mapped',
  sync_error: 'Could Not Import',
  status: 'Unrecognised Status',
  bank_amount_cents: 'Bank Amount Changed',
  cash_date: 'Cash Date Changed',
  counterparty: 'Counterparty Changed',
};

function labelFor(field: string): string {
  return EXCEPTION_LABEL[field] ?? field.replace(/_/g, ' ');
}

export function SyncStatusPage() {
  // What this screen offers (0069); the API refuses the rest either way.
  const can = useCan();
  const mayEdit = can('edit_entries');
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exceptions, setExceptions] = useState<SyncException[] | null>(null);
  const [exceptionsError, setExceptionsError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState<ManualSyncResult[] | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  /*
   * SYNC NOW (issue #54). The route existed and nothing on screen called it, so
   * a new connection waited for the next tick and a failure could only be
   * learned by waiting a day. Each bank's answer is shown in full: what it
   * fetched and created, or Mercury's own words when it refused -- which is
   * the "why is it failing" the banner could not say.
   */
  async function syncNow() {
    setSyncing(true);
    setSyncError(null);
    setSyncResults(null);
    try {
      setSyncResults((await api.syncNow()).banks);
      setStatus(await api.syncStatus());
      // The banner above reads the same status, and must not go on saying
      // "not synced yet" over a sync that just ran (review SN-1).
      announceSyncChanged();
    } catch (caught) {
      setSyncError(caught instanceof ApiError ? caught.message : 'Could not run the sync.');
    } finally {
      setSyncing(false);
    }
  }

  useEffect(() => {
    api
      .syncStatus()
      .then(setStatus)
      .catch((caught: unknown) =>
        // error.message, not error.code: it is the server's sentence when it
        // sent one and falls back to the code when it did not, so this is
        // never worse than what it replaced and is usually readable.
        setError(caught instanceof ApiError ? caught.message : 'unexpected_error'),
      );
    api
      .listSyncExceptions()
      .then((result) => setExceptions(result.exceptions))
      .catch((caught: unknown) =>
        setExceptionsError(caught instanceof ApiError ? caught.message : 'Could not list the exceptions.'),
      );
  }, []);

  async function markHandled(id: string) {
    setBusy(true);
    setExceptionsError(null);
    try {
      await api.resolveSyncException(id);
      setExceptions((current) => (current ?? []).filter((row) => row.id !== id));
      setStatus((current) =>
        current ? { ...current, openExceptions: Math.max(0, current.openExceptions - 1) } : current,
      );
      setConfirming(null);
    } catch (caught) {
      setExceptionsError(caught instanceof ApiError ? caught.message : 'Could not mark that handled.');
    } finally {
      setBusy(false);
    }
  }

  if (error) return <p role="alert" className="error">Could not load sync status ({error}).</p>;
  if (!status) return <p className="muted">Loading…</p>;

  return (
    <>
      <h2>Bank feeds</h2>
      <Fact
        label="Schedule"
        value={
          status.scheduleRegistered
            ? 'running'
            : status.scheduleEnabled
              ? 'enabled, but did not start'
              : 'off'
        }
      />
      <Fact label="Open exceptions" value={String(status.openExceptions)} />

      {status.banks.some((bank) => !bank.disabled) && (
        <div className="row">
          {mayEdit && (
            <button type="button" disabled={syncing} onClick={() => void syncNow()}>
              {syncing ? 'Syncing…' : 'Sync Now'}
            </button>
          )}
          <span className="muted">
            {status.scheduleRegistered
              ? `The schedule runs ${status.scheduleDescription}.`
              : 'The scheduled sync is not running, so Sync Now is the only way the banks sync.'}
          </span>
        </div>
      )}
      {syncError && (
        <p role="alert" className="error">
          {syncError}
        </p>
      )}
      {syncResults && (
        <ul className="sync-results" aria-label="Sync Results">
          {syncResults.map((result) => (
            <li key={result.connectionId}>
              <strong>{result.bank}</strong>:{' '}
              {result.ran
                ? `fetched ${result.fetched ?? 0}, created ${result.created ?? 0}, updated ${result.updated ?? 0}` +
                  (result.exceptions ? `, ${result.exceptions} for you to look at` : '') +
                  '.'
                : result.error
                  ? <span className="error">failed. Mercury said: {result.error}</span>
                  : (result.reason ?? 'did not run.')}
            </li>
          ))}
        </ul>
      )}

      {/*
        * ONE BLOCK PER BANK (0046).
        *
        * The status used to be a single set of facts. With two banks that
        * shape can only show one of them, and the one it showed would be
        * whichever the server picked -- which is the silent half-outage
        * `resolveSyncTenant` refused to allow, arriving through this screen.
        */}
      {status.banks.length === 0 ? (
        <p className="muted">
          No bank is connected to this business yet, so nothing is being fetched.
        </p>
      ) : (
        status.banks.map((bank) => (
          <section key={bank.connectionId} className="settings-section">
            <h3>
              {bank.bank}
              {bank.disabled ? ' (turned off)' : ''}
            </h3>
            <dl className="facts">
              <Fact label="Environment" value={bank.environment} />
              <Fact label="Accounts mapped" value={String(bank.mappedAccounts)} />
              <Fact
                label="Last run"
                value={
                  bank.lastRunAt
                    ? formatTimestamp(bank.lastRunAt)
                    : status.scheduleRegistered
                      ? `not yet; the schedule runs ${status.scheduleDescription}`
                      : 'not yet; only Sync Now runs it'
                }
              />
              <Fact label="Last run result" value={bank.lastRunStatus ?? '—'} />
              <Fact label="Triggered by" value={bank.lastRunTrigger ?? '—'} />
              <Fact
                label="Last success"
                value={bank.lastSuccessAt ? formatTimestamp(bank.lastSuccessAt) : 'never'}
              />
              <Fact
                label="Token deleted in"
                value={
                  bank.daysUntilTokenDeleted === null
                    ? 'no runs yet'
                    : `${bank.daysUntilTokenDeleted} days` +
                      (bank.tokenCountdownFrom === 'first_attempt' ? ' (from first attempt)' : '')
                }
              />
            </dl>
            {bank.lastRunError && (
              <>
                <h4>Last error</h4>
                <pre className="error-detail">{bank.lastRunError}</pre>
              </>
            )}
            {bank.lastRunNote && (
              <>
                <h4>Note From The Last Run</h4>
                <p className="muted">The last sync succeeded; {bank.lastRunNote}.</p>
              </>
            )}
          </section>
        ))
      )}

      <section className="sync-exceptions" aria-labelledby="sync-exceptions">
        <h3 id="sync-exceptions">Exceptions</h3>
        <p className="muted">
          What the sync found and would not decide on its own. Correct the entry if it needs it;
          marking an exception handled only takes it off this list and changes nothing in the books.
        </p>
        {exceptionsError && (
          <p role="alert" className="error">
            {exceptionsError}
          </p>
        )}
        {exceptions === null ? (
          !exceptionsError && <p className="muted">Loading…</p>
        ) : exceptions.length === 0 ? (
          <p className="muted">No open exceptions.</p>
        ) : (
          <ul className="exception-list" aria-label="Open exceptions">
            {exceptions.map((row) => (
              <li key={row.id}>
                <p>
                  <strong>{labelFor(row.field)}</strong>{' '}
                  <span className="muted">· found {formatTimestamp(row.detectedAt)}</span>
                </p>
                <p>
                  {row.headerId && (
                    <>
                      <Link to={`/journals/${row.headerId}`}>
                        {[row.entryDate, row.counterparty].filter(Boolean).join(' ') || 'Open Entry'}
                      </Link>
                      {' · '}
                    </>
                  )}
                  Was {row.oldValue ?? '—'} · Now {row.newValue ?? '—'}
                </p>
                {confirming === row.id ? (
                  <div className="new-account undo-form" role="group" aria-label="Mark Handled">
                    <p>
                      Mark this handled? It comes off this list. Nothing in the books changes, so
                      correct the entry first if it needs it.
                    </p>
                    <div className="row">
                      <button type="button" disabled={busy} onClick={() => void markHandled(row.id)}>
                        Mark It Handled
                      </button>
                      <button type="button" className="link" onClick={() => setConfirming(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  mayEdit && (
                    <button type="button" className="link" onClick={() => setConfirming(row.id)}>
                      Mark Handled
                    </button>
                  )
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="fact">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
